import { Button, Icon, TextInput } from 'lego-on-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncFn } from 'react-use';

import i18n from '@yandex-int/i18n';
import { userAgreementsComparator, userAgreementsValidators, ValidationErrResult } from 'client/utils/validators';
import * as keyset from 'i18n/Settings';
import block from 'propmods';

import { getAgreements } from '../../api';
import { useSettingsPageContext } from '../../context/settingsPageContext';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { locateError } from '../../lib/validate';
import { formUARequests, UserAgreementDraft, UserAgreementOrDraft } from '../../model/userAgreement';
import { FetchResult } from '../FetchResult';
import { Field } from '../Form/Form';
import { FormError } from '../Form/FormError';
import { OrderPick } from '../OrderPick';

import './UserAgreements.scss';

const b = block('UserAgreements');
const t = i18n(keyset);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const extractError = (e: any) => (Object.values(e.fields ?? {})[0] ?? t('ua-error-unknown')) as string;

type UAUpdateCb = (_agreements: UserAgreementOrDraft[]) => UserAgreementOrDraft[];

type UAFieldProps = {
    name: string;
    label: string;
    error: string | undefined;
    text: string;
    isEditable: boolean;
    onChange: (_v: string) => void;
};
const UAField = ({ name, error, label, onChange, text, isEditable }: UAFieldProps) => {
    const [hasChanged, setHasChanged] = useState(false);
    const onChangeCb = useCallback(
        (v: string) => {
            setHasChanged(true);
            onChange(v);
        },
        [onChange],
    );
    return (
        <Field required name={name} label={label} error={hasChanged ? error : undefined}>
            <TextInput disabled={!isEditable} theme="normal" size="s" text={text} onChange={onChangeCb} />
        </Field>
    );
};

type UserAgreementsRowsProps = {
    isEditable: boolean;
    data: UserAgreementOrDraft[];
    onUpdate: (_cb: UAUpdateCb) => void;
    errors: ValidationErrResult[];
};
const UserAgreementsRows = ({ data, errors, isEditable, onUpdate }: UserAgreementsRowsProps) => {
    const updateItem = (idx: number, field: keyof UserAgreementDraft) => (value: string) =>
        onUpdate((data) => [...data.slice(0, idx), { ...data[idx], [field]: value }, ...data.slice(idx + 1)]);

    const errorLocator = useMemo(() => locateError(errors), [errors]);

    const onNext = React.useCallback(
        (idx: number) => {
            onUpdate((data) => [...data.slice(0, idx), data[idx + 1], data[idx], ...data.slice(idx + 2)]);
        },
        [onUpdate],
    );

    const onPrev = React.useCallback(
        (idx: number) => {
            onUpdate((data) => [...data.slice(0, idx - 1), data[idx], data[idx - 1], ...data.slice(idx + 1)]);
        },
        [onUpdate],
    );

    const onRemove = React.useCallback(
        (idx: number) => {
            onUpdate((data) => data.filter((_, itemIdx) => itemIdx !== idx));
        },
        [onUpdate],
    );

    return (
        <div {...b('rows')}>
            {data.map(({ name, url }, idx) => (
                <div {...b('row')} key={idx}>
                    {isEditable && (
                        <OrderPick
                            order={idx}
                            disablePrev={idx === 0}
                            disableNext={idx === data.length - 1}
                            onNext={onNext}
                            onPrev={onPrev}
                        />
                    )}
                    <div {...b('fields')}>
                        <UAField
                            isEditable={isEditable}
                            name="name"
                            label={t('ua-name')}
                            text={name}
                            error={errorLocator({ field: 'name', idx })?.error}
                            onChange={updateItem(idx, 'name')}
                        />
                        <UAField
                            isEditable={isEditable}
                            name="url"
                            label={t('ua-url')}
                            error={errorLocator({ field: 'url', idx })?.error}
                            text={url}
                            onChange={updateItem(idx, 'url')}
                        />
                    </div>
                    {isEditable && (
                        <div {...b('remove')} onClick={() => onRemove(idx)}>
                            <Icon type="cross" />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export const UserAgreements = () => {
    const skill = useCurrentAliceApp();
    const { isDraft, isEditable } = useSettingsPageContext();
    const [agreements, setAgreements] = useState<UserAgreementOrDraft[]>(() => []);
    const [fetchedAgreements, onFetch] = useAsyncFn(() =>
        getAgreements(skill?.id ?? '', Boolean(isDraft)).then((result) => result.sort(userAgreementsComparator)),
    );

    useEffect(() => {
        onFetch();
    }, [onFetch]);

    const onAdd = React.useCallback(() => {
        setAgreements((agreements) => [
            ...agreements,
            {
                name: '',
                url: '',
                order: agreements.length,
            },
        ]);
    }, []);

    useEffect(() => {
        if (Array.isArray(fetchedAgreements.value)) {
            if (fetchedAgreements.value.length === 0) {
                onAdd();
            } else {
                setAgreements(fetchedAgreements.value);
            }
        }
    }, [fetchedAgreements.value, onAdd]);

    const [submitState, onSubmit] = useAsyncFn(async () => {
        await formUARequests(skill?.id ?? '', fetchedAgreements.value ?? [], agreements);
        await onFetch();
    }, [skill?.id, agreements, fetchedAgreements.value]);

    const validity = useMemo(() => userAgreementsValidators(agreements), [agreements]);

    const onUpdate = useCallback((cb: UAUpdateCb) => {
        setAgreements(cb);
    }, []);

    return (
        <div {...b()}>
            <FetchResult data={fetchedAgreements}>
                <UserAgreementsRows
                    isEditable={Boolean(isEditable)}
                    data={agreements}
                    onUpdate={onUpdate}
                    errors={validity}
                />
                {isEditable && (
                    <>
                        <Button theme="normal" size="s" onClick={onAdd}>
                            {t('ua-add')}
                        </Button>
                        &nbsp;&nbsp;
                        <Button
                            theme="action"
                            size="s"
                            onClick={onSubmit}
                            progress={submitState.loading}
                            disabled={validity.length > 0}
                        >
                            {t('ua-save')}
                        </Button>
                    </>
                )}
            </FetchResult>
            {submitState.error && <FormError>{extractError(submitState.error)}</FormError>}
        </div>
    );
};
